import { openSupportTask } from '../services/supportTaskNavigation';
import React, { useState, useEffect, useRef } from 'react';
import {
  Bell,
  X,
  Check,
  TrendingUp,
  Users,
  Calendar,
  CreditCard,
  FileText,
  Mail,
  RefreshCw,
  Building2,
  User,
  CheckCheck,
  CheckCircle
} from 'lucide-react';
import { Notification as AppNotification } from '../services/notificationService';
import { useNotifications } from '../hooks/useNotifications';

interface NotificationCenterProps {
  onNavigate?: (tab: string) => void;
}

const getNotificationLabel = (code: string): string => {
  const labels: Record<string, string> = {
    'LEAD.NEW': 'Nuevo Lead',
    'LEAD.ASSIGN': 'Lead Asignado',
    'LEAD.CONVERTED': 'Lead Convertido',
    'CONTACT.CREATED': 'Cliente Creado',
    'COMPANY.CREATED': 'Empresa Creada',
    'MEETING.SCHEDULED': 'Reunión Programada',
    'MEETING.APPROVED': 'Reunión Aprobada',
    'MEETING.CANCELLED': 'Reunión Cancelada',
    'MEETING.APPROVAL_REQUIRED': 'Reunión Pendiente de Aprobación',
    'ACTIVITY.CREATED': 'Actividad Registrada',
    'FORM.SUBMISSION': 'Formulario Recibido',
    'FORM.RESPONSE': 'Respuesta de Formulario',
    'TIMESHEET.SUBMIT': 'Horas Enviadas',
    'TIMESHEET.APPROVE': 'Horas Aprobadas',
    'TIMESHEET.APPROVED': 'Horas Aprobadas',
    'INVOICE.CREATED': 'Factura Nueva',
    'INVOICE.VALIDATED': 'Factura Validada',
    'PAYMENT.EXECUTED': 'Pago Ejecutado',
    'TIMESHEET.REJECT': 'Horas Rechazadas',
    'INVOICE.COLLAB.UPLOAD': 'Factura Subida',
    'PAYMENT.SCHEDULED': 'Pago Programado',
    'PAYMENT.DUE': 'Pago Pendiente',
    'PAYMENT.SENT': 'Pago Ejecutado',
    'INVOICE.OVERDUE': 'Factura Vencida',
    'QONTO.TX.INCOME': 'Ingreso Detectado',
    'QONTO.INCOME.UNASSIGNED': 'Ingreso Sin Asignar',
    'QONTO.BANK.TX_IN': 'Transferencia Recibida',
    'QONTO.BANK.TX_OUT': 'Transferencia Enviada',
    'TASK.DUE_TODAY': 'Tarea Vence Hoy',
    'PROJECT.MILESTONE.DUE': 'Hito Próximo',
    'PROJECT.LATE': 'Proyecto Retrasado',
    'PROJECT.ASSIGNED': 'Nueva asignación',
    'PROJECT.CREATED': 'Proyecto Creado',
    'TASK.STATUS_CHANGED': 'Cambio de estado en tarea',
    'CONTRACT.RENEW.SOON': 'Contrato por Renovar',
    'DOC.SIGNED': 'Documento Firmado',
    'USER.CREATED': 'Usuario Creado',
    'CONTRACT.SIGNED': 'Contrato Firmado',
    'PROPOSAL.ACCEPTED': 'Propuesta Aceptada',
    'SYSTEM.UPDATE': 'Actualización del Sistema',
    'DIGEST.DAILY.ADMIN': 'Resumen Diario',
    'DIGEST.WEEKLY.PM': 'Resumen Semanal'
  };

  return labels[code] || code;
};

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onNavigate }) => {
  const {
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAllAsRead
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | 'critical'>('all');
  const [markAllSuccess, setMarkAllSuccess] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getNotificationIcon = (code: string) => {
    if (!code) return Bell;
    if (code.startsWith('LEAD.')) return Users;
    if (code.startsWith('CONTACT.')) return Users;
    if (code.startsWith('COMPANY.')) return Building2;
    if (code.startsWith('MEETING.') || code.startsWith('TASK.')) return Calendar;
    if (code.startsWith('CONTRACT.') || code.startsWith('PROPOSAL.') || code.startsWith('DOC.')) return FileText;
    if (code.startsWith('INVOICE.') || code.startsWith('BILLING.')) return FileText;
    if (code.startsWith('QONTO.')) return TrendingUp;
    if (code.startsWith('PAYMENT.')) return CreditCard;
    if (code.startsWith('DIGEST.')) return Mail;
    if (code.startsWith('USER.')) return User;
    return Bell;
  };

  const getNotificationColor = (severity: string, isRead: boolean) => {
    if (!severity) return 'text-gray-600 bg-gray-100';
    if (isRead) return 'text-gray-600 bg-gray-100';

    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'warn': return 'text-orange-600 bg-orange-100';
      case 'info': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity: string) => {
    if (!severity) return 'bg-gray-100 text-gray-800';
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'warn': return 'bg-orange-100 text-orange-800';
      case 'info': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  const handleNotificationClick = async (notification: AppNotification) => {
    if (!notification.read_at) {
      await markAsRead(notification.id);
    }

    if (notification.code === 'SUPPORT.CASE_CREATED' && openSupportTask(notification.data)) { setIsOpen(false); return; }
    let targetPath = '';

    // Paso 1: resolver la tab destino. deep_link tiene prioridad si viene
    // de una notificación con un mapa concreto, pero por código sabemos
    // afinar más (p.ej. Leads, Contactos) cuando no hay deep_link útil.
    if (notification.deep_link && notification.deep_link !== '/dashboard') {
      targetPath = notification.deep_link.replace('/', '');
    } else if (notification.code.startsWith('LEAD.')) {
      targetPath = 'leads';
    } else if (notification.code === 'MEETING.APPROVAL_REQUIRED') {
      targetPath = 'reuniones';
    } else if (notification.code.startsWith('MEETING.')) {
      targetPath = 'reuniones';
    } else if (notification.code.startsWith('CONTACT.')) {
      targetPath = 'contactos';
    } else if (notification.code.startsWith('COMPANY.')) {
      targetPath = 'contactos';
    } else if (notification.code.startsWith('ACTIVITY.')) {
      targetPath = 'actividades';
    } else if (notification.code.startsWith('TIMESHEET.') || notification.code.startsWith('INVOICE.') || notification.code.startsWith('PAYMENT.')) {
      targetPath = 'pagos';
    } else if (notification.code === 'TASK.STATUS_CHANGED') {
      targetPath = 'pagos';
    } else if (notification.code.startsWith('QONTO.')) {
      targetPath = 'pagos';
    } else if (notification.code.startsWith('USER.')) {
      targetPath = 'usuarios';
    } else if (notification.code.startsWith('FORM.')) {
      targetPath = 'formularios';
    } else if (notification.code.startsWith('PROPOSAL.')) {
      targetPath = 'presupuestos';
    } else if (notification.code.startsWith('CONTRACT.')) {
      targetPath = 'mantenimiento';
    }

    // Paso 2: side-effects (sub-tab, apertura de detalle) siempre por código,
    // independiente de quién resolvió el targetPath.
    if (notification.code === 'TIMESHEET.APPROVED') {
      window.dispatchEvent(new CustomEvent('navigate-to-payments-tab', {
        detail: { subTab: 'invoices' }
      }));
    } else if (notification.code === 'INVOICE.CREATED' || notification.code === 'INVOICE.UPDATED' || notification.code === 'INVOICE.REJECTED' || notification.code === 'INVOICE.VALIDATED') {
      window.dispatchEvent(new CustomEvent('navigate-to-payments-tab', {
        detail: { subTab: 'invoices' }
      }));
    } else if (notification.code === 'PAYMENT.EXECUTED' || notification.code === 'INVOICE.PAID') {
      window.dispatchEvent(new CustomEvent('navigate-to-payments-tab', {
        detail: { subTab: 'payments' }
      }));
    } else if (notification.code.startsWith('TIMESHEET.')) {
      window.dispatchEvent(new CustomEvent('navigate-to-timesheet', {
        detail: { timesheetId: notification.data?.timesheet_id }
      }));
    } else if (notification.code === 'TASK.STATUS_CHANGED') {
      if (notification.data?.project_id) {
        window.dispatchEvent(new CustomEvent('navigate-to-project', {
          detail: { projectId: notification.data.project_id }
        }));
      }
    }

    if (targetPath && onNavigate) {
      onNavigate(targetPath);
    }

    setIsOpen(false);
  };

  const handleMarkAllAsReadClick = async () => {
    if (unreadCount === 0) return;
    try {
      await markAllAsRead();
      setMarkAllSuccess(true);
      setTimeout(() => setMarkAllSuccess(false), 2000);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.read_at;
    if (filter === 'critical') return notification.severity === 'critical';
    return true;
  });

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Notificaciones</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={loadNotifications}
                  disabled={loading}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex space-x-2 mt-3">
              {[
                { value: 'all', label: 'Todas' },
                { value: 'unread', label: 'No leídas' },
                { value: 'critical', label: 'Críticas' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setFilter(option.value as any)}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    filter === option.value
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-gray-500 text-sm">Cargando notificaciones...</p>
              </div>
            ) : filteredNotifications.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filteredNotifications.map((notification) => {
                  const Icon = getNotificationIcon(notification.code);
                  const colorClass = getNotificationColor(notification.severity, !!notification.read_at);

                  return (
                    <div
                      key={notification.id}
                      className={`p-4 cursor-pointer transition-colors ${
                        !notification.read_at
                          ? 'bg-blue-50 border-l-4 border-blue-500 hover:bg-blue-100'
                          : 'bg-white border-l-4 border-transparent hover:bg-gray-50'
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm font-medium ${
                              !notification.read_at ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </p>
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(notification.severity)}`}>
                              {notification.severity === 'info' ? 'Info' :
                               notification.severity === 'warn' ? 'Aviso' :
                               notification.severity === 'critical' ? 'Crítico' : notification.severity}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatTimestamp(notification.created_at)}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.body}
                          </p>

                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-400 font-mono">
                              {getNotificationLabel(notification.code)}
                            </span>
                            {!notification.read_at && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                              >
                                <Check className="w-3 h-3" />
                                <span>Marcar leída</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  {filter === 'unread' ? 'No hay notificaciones sin leer' :
                   filter === 'critical' ? 'No hay notificaciones críticas' :
                   'No hay notificaciones'}
                </h4>
                <p className="text-gray-500">
                  {filter === 'all' && 'Las notificaciones aparecerán aquí cuando ocurran eventos'}
                </p>
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                {markAllSuccess ? (
                  <div className="flex items-center space-x-1 text-sm text-green-600 font-medium">
                    <CheckCircle className="w-4 h-4" />
                    <span>Todas marcadas como leídas</span>
                  </div>
                ) : (
                  <button
                    onClick={handleMarkAllAsReadClick}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={unreadCount === 0}
                  >
                    <div className="flex items-center space-x-1">
                      <CheckCheck className="w-3 h-3" />
                      <span>Marcar todas como leídas ({unreadCount})</span>
                    </div>
                  </button>
                )}
                <div className="text-xs text-gray-500">
                  {filteredNotifications.length} de {notifications.length}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
